export interface LogSignature {
  timestamp: string;
  signature: string;
}

const MAX_SKEW_MS = 5 * 60_000;

export async function signLogPayload(
  body: string,
  timestamp: string,
  secret: string
): Promise<LogSignature> {
  if (!body || !/^\d+$/.test(timestamp) || !secret) {
    throw new Error('Invalid HMAC signing input');
  }

  const encoder = new TextEncoder();
  const key = await globalThis.crypto.subtle.importKey(
    'raw',
    encoder.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  const signature = await globalThis.crypto.subtle.sign(
    'HMAC',
    key,
    encoder.encode(`${timestamp}.${body}`)
  );

  return {
    timestamp,
    signature: Array.from(new Uint8Array(signature))
      .map((value) => value.toString(16).padStart(2, '0'))
      .join(''),
  };
}

export async function verifyLogSignature(
  body: string,
  timestamp: string,
  signature: string,
  secret: string,
  maxSkewMs = MAX_SKEW_MS
): Promise<boolean> {
  if (!body || !/^\d+$/.test(timestamp) || !signature || !secret) {
    return false;
  }
  if (Math.abs(Date.now() - Number(timestamp)) > maxSkewMs) {
    return false;
  }

  try {
    const expected = await signLogPayload(body, timestamp, secret);
    const expectedBytes = Uint8Array.from(
      expected.signature.match(/.{2}/g) || [],
      (value) => Number.parseInt(value, 16)
    );
    const actualBytes = Uint8Array.from(
      signature.toLowerCase().match(/.{2}/g) || [],
      (value) => Number.parseInt(value, 16)
    );
    if (expectedBytes.length !== actualBytes.length) {
      return false;
    }

    let difference = 0;
    for (let index = 0; index < expectedBytes.length; index += 1) {
      const expectedByte = expectedBytes[index] ?? 0;
      const actualByte = actualBytes[index] ?? 0;
      difference |= expectedByte ^ actualByte;
    }
    return difference === 0;
  } catch (_err) {
    /* intentional: any signature-parsing error means verification fails closed */
    return false;
  }
}

/**
 * Sign and POST a payload to `<hostURL>/api/log` with HMAC headers. Uses
 * `fetch({ keepalive: true })` so the request survives page unloads. Errors
 * are swallowed — remote logging must never crash the caller.
 *
 * Consolidates the identical send-log logic that used to live in bread-sdk
 * and financing-sdk.
 */
export function sendSignedLog(
  hostURL: string,
  secret: string,
  payload: unknown
): void {
  const body = JSON.stringify(payload);
  void signLogPayload(body, Date.now().toString(), secret)
    .then(({ timestamp, signature }) =>
      fetch(`${hostURL}/api/log`, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          'x-log-timestamp': timestamp,
          'x-log-signature': signature,
        },
        body,
      })
    )
    .catch((_err) => {
      /* intentional: logger errors must never crash the caller */
    });
}
